import React, { useState, useEffect, useRef } from "react";
import { Area } from "../models/Area";
import { computeAreaProgress } from "../utils/areaProgress";
import { useAuth } from "../context/AuthContext";
import { saveAreaRatingsDiff } from "../services/ratingsService";
import { colors, fonts, radius } from "../utils/colors";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Collapse from '@mui/material/Collapse';
import LinearProgress from '@mui/material/LinearProgress';

interface Props {
  area: Area;
  ratings: Record<string, number>;
  activeDraftId: number;
  setRatings: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  isAreaComplete?: (area: Area, ratings: Record<string, number>) => boolean;
//  requestPeriodid?: number;
}

const ratingOptions = [
  { value: 0, label: "0. Not at all" },
  { value: 1, label: "1. To little extent" },
  { value: 2, label: "2. Partially" },
  { value: 3, label: "3. To great extent" },
  { value: 4, label: "4. Almost completely" },
  { value: 5, label: "5. Completely" },
];

const AreasGrid: React.FC<Props> = ({ area, ratings, activeDraftId, setRatings, isAreaComplete })  => {
  const subAreas = area.subArea ?? [];
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});

  const isSubAreaComplete = (subArea: Area["subArea"][number]) =>
    (subArea.subAreaDetails ?? []).every(
      detail => typeof ratings[`${subArea.subAreaId}-${detail.subAreaDetailId}`] === "number" &&
                ratings[`${subArea.subAreaId}-${detail.subAreaDetailId}`] !== -1
    );

  const toggleSection = (subAreaId: number) => {
    setExpandedSections(prev => ({ ...prev, [subAreaId]: !prev[subAreaId] }));
  };

  const isSectionExpanded = (subAreaId: number) => {
    if (expandedSections[subAreaId] !== undefined) {
      return expandedSections[subAreaId];
    }
    // Default: collapsed if complete, expanded if not
    const subArea = subAreas.find(sa => sa.subAreaId === subAreaId);
    return subArea ? !isSubAreaComplete(subArea) : true;
  };

  // NEW: track which keys were actually changed by the user
  const dirtyKeysRef = useRef<Set<string>>(new Set());
  // NEW: mark that we have initialized the snapshot (so we don’t autosave initial load)
  const initialLoadedRef = useRef(false);

  const handleRatingChange = (subAreaId: number, detailId: number, value: number) => {
    const key = `${subAreaId}-${detailId}`;
    setRatings(prev => ({
      ...prev,
      [key]: value,
    }));
    dirtyKeysRef.current.add(key);        // only user-touched keys
    setSaveState('pending');
  };

  // Track last successfully sent ratings for this area (diff logic)
  const lastSentAreaRatings = useRef<Record<string, number | null>>({});

  // INIT: when component mounts (or area changes) treat current ratings as already sent
  useEffect(() => {
    // Build baseline only once per area id mount
    if (!initialLoadedRef.current) {
      subAreas.forEach(subArea => {
        (subArea.subAreaDetails ?? []).forEach(detail => {
          const key = `${subArea.subAreaId}-${detail.subAreaDetailId}`;
          const raw = ratings[key];
          const currentValue = typeof raw === 'number' && raw >= 0 ? raw : null;
          lastSentAreaRatings.current[key] = currentValue;
        });
      });
      initialLoadedRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area.areaId]);

  // Build diff payload: only changed items (dirty keys) whose value differs from last sent
  const prepareRatingsForBackend = () => {
    if (!initialLoadedRef.current) return [];
    const changes: {
      areaId: number;
      subAreaId: number;
      subAreaDetailId: number;
      rating: number | null;
    }[] = [];

    dirtyKeysRef.current.forEach(key => {
      const [subAreaIdStr, detailIdStr] = key.split("-");
      const subAreaId = Number(subAreaIdStr);
      const detailId = Number(detailIdStr);
      const raw = ratings[key];
      const currentValue = typeof raw === 'number' && raw >= 0 ? raw : null;
      const prevValue = lastSentAreaRatings.current[key];

      if (currentValue !== prevValue) {
        // Include clearing (null) only if it was previously non-null
        const shouldInclude =
          currentValue !== null || (prevValue !== undefined && prevValue !== null);
        if (shouldInclude) {
          changes.push({
            areaId: area.areaId,
            subAreaId,
            subAreaDetailId: detailId,
            rating: currentValue
          });
        }
      }
    });

    return changes;
  };

  // Collect keys for this area only
  const areaDetailKeys = subAreas.flatMap(sa =>
    (sa.subAreaDetails ?? []).map(d => `${sa.subAreaId}-${d.subAreaDetailId}`)
  );

  // Progress
  const { ratedCount, totalCount, percent, complete } = computeAreaProgress(area, ratings);

  // Autosave state
  const [saveState, setSaveState] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const timerRef = useRef<number | undefined>(undefined);

  // Serialize current area ratings for dependency tracking
  const areaRatingsSnapshot = JSON.stringify(
    areaDetailKeys.reduce<Record<string, number | undefined>>((acc, k) => {
      acc[k] = ratings[k];
      return acc;
    }, {})
  );

  const { userId, getAccessToken, isLoggedIn } = useAuth();

  useEffect(() => {
    if (saveState === 'pending') {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(async () => {
        //if (!requestPeriodid) {
        //  setSaveState('error');
        //  return;
        //}
        const rawChanges = prepareRatingsForBackend();
        if (rawChanges.length === 0) {
          setSaveState('idle');
          return;
        }
        setSaveState('saving');
        try {
          if (!isLoggedIn) {
            setSaveState('idle');
            return;
          }
          if (activeDraftId == null) {
            setSaveState('error');
            return;
          }

          const diff = rawChanges.map(item => ({
            areaId: item.areaId,
            subAreaId: item.subAreaId,
            subAreaDetailId: item.subAreaDetailId,
            rating: item.rating === null ? -1 : item.rating
          }));

          const saveBody = {
            userId: typeof userId === "string" ? 0 : userId || 0,
            requestPeriodId: activeDraftId,
            changes: diff
          };

          const result = await saveAreaRatingsDiff( saveBody, getAccessToken);

          if (!result.ok) {
            setSaveState('error');
            return;
          }

          // Update last sent snapshot and clear dirty set only for changed keys
            rawChanges.forEach(item => {
              const k = `${item.subAreaId}-${item.subAreaDetailId}`;
              lastSentAreaRatings.current[k] = item.rating;
              dirtyKeysRef.current.delete(k);
            });

          setSaveState('saved');
          setTimeout(() => setSaveState(s => (s === 'saved' ? 'idle' : s)), 1200);
        } catch (e) {
          setSaveState('error');
        }
      }, 600);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [saveState, prepareRatingsForBackend, isLoggedIn, area.areaId, getAccessToken, userId]);

  const statusLabel = {
    idle: '',
    pending: 'Pending save…',
    saving: 'Saving…',
    saved: 'Saved',
    error: 'Save error'
  }[saveState];

  return (
    <Box sx={{ fontFamily: fonts.main }}>
      {/* Area Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
        <Typography variant="h5" sx={{ color: colors.darkBlue, fontWeight: 600 }}>
          {area.areaName}
        </Typography>
        {area.areaKeywords && (
          <Chip
            label={area.areaKeywords}
            size="small"
            sx={{
              backgroundColor: colors.pink,
              color: colors.darkBlue,
              fontWeight: 500,
              fontSize: "0.75rem",
              borderRadius: `${radius.sm}px`,
            }}
          />
        )}
      </Box>
      <Typography variant="body2" sx={{ color: colors.mediumGray, mb: 2 }}>
        {area.areaDescription}
      </Typography>

      {/* SubAreas */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {subAreas.map(subArea => {
          const subComplete = isSubAreaComplete(subArea);
          return (
            <Box
              key={subArea.subAreaId}
              sx={{
                borderRadius: `${radius.lg}px`,
                border: `1px solid ${subComplete ? colors.green : colors.lightGray}`,
                backgroundColor: subComplete ? "rgba(2, 211, 95, 0.04)" : colors.white,
                overflow: "hidden",
                transition: "border-color 0.2s ease, background-color 0.2s ease",
              }}
            >
              {/* SubArea Header */}
              <Box
                onClick={() => toggleSection(subArea.subAreaId)}
                sx={{
                  px: 2,
                  py: 0.5,
                  backgroundColor: subComplete ? "rgba(2, 211, 95, 0.08)" : colors.offWhite,
                  borderBottom: isSectionExpanded(subArea.subAreaId) ? `1px solid ${subComplete ? "rgba(2, 211, 95, 0.2)" : colors.lightGray}` : "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { backgroundColor: subComplete ? "rgba(2, 211, 95, 0.12)" : colors.lightGray },
                  transition: "background-color 0.15s ease",
                }}
              >
                <ExpandMoreIcon
                  sx={{
                    fontSize: 18,
                    color: colors.mediumGray,
                    transform: isSectionExpanded(subArea.subAreaId) ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.darkBlue, flex: 1 }}>
                  {subArea.subAreaName}
                </Typography>
                {subComplete && (
                  <CheckCircleIcon sx={{ color: colors.green, fontSize: 16 }} />
                )}
              </Box>

              {/* SubArea Details */}
              <Collapse in={isSectionExpanded(subArea.subAreaId)}>
              <Box sx={{ px: 2, py: 0.25 }}>
                {(subArea.subAreaDetails ?? []).map((detail, idx) => {
                  const key = `${subArea.subAreaId}-${detail.subAreaDetailId}`;
                  const rating = ratings[key] ?? -1;
                  const isRated = rating > -1;
                  return (
                    <Box
                      key={detail.subAreaDetailId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        py: 0.4,
                        borderBottom: idx < (subArea.subAreaDetails?.length ?? 0) - 1 ? `1px solid ${colors.offWhite}` : "none",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.mediumGray,
                          fontFamily: fonts.mono,
                          minWidth: 24,
                          textAlign: "center",
                        }}
                      >
                        {detail.subAreaDetailId}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ flex: 1, color: colors.darkBlue, lineHeight: 1.4, fontSize: "0.82rem" }}
                      >
                        {detail.description}
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select
                          value={rating}
                          onChange={e =>
                            handleRatingChange(
                              subArea.subAreaId,
                              detail.subAreaDetailId,
                              Number(e.target.value)
                            )
                          }
                          sx={{
                            fontFamily: fonts.main,
                            fontSize: "0.8rem",
                            height: 32,
                            borderRadius: `${radius.md}px`,
                            backgroundColor: isRated ? "rgba(2, 211, 95, 0.06)" : colors.white,
                            borderColor: isRated ? colors.green : colors.lightGray,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: isRated ? colors.green : colors.lightGray,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: isRated ? colors.teal : colors.turquoise,
                            },
                          }}
                        >
                          <MenuItem value={-1} sx={{ color: colors.mediumGray, fontStyle: "italic" }}>
                            Make a choice
                          </MenuItem>
                          {ratingOptions.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  );
                })}
              </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Progress Footer */}
      <Box sx={{ mt: 3, textAlign: "right" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "flex-end", mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{
              flex: 1,
              maxWidth: 200,
              height: 6,
              borderRadius: 3,
              backgroundColor: colors.lightGray,
              "& .MuiLinearProgress-bar": {
                backgroundColor: complete ? colors.green : colors.teal,
                borderRadius: 3,
              },
            }}
          />
          <Typography variant="body2" sx={{ color: colors.mediumGray, fontFamily: fonts.mono, fontSize: "0.85rem" }}>
            {ratedCount}/{totalCount} ({percent}%)
          </Typography>
        </Box>
        {statusLabel && (
          <Typography
            variant="caption"
            sx={{
              color: saveState === 'error' ? colors.error : colors.mediumGray,
              fontStyle: saveState === 'saving' ? 'italic' : 'normal',
            }}
          >
            {statusLabel}
          </Typography>
        )}
        {complete && (
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: colors.green }}>
            Done
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AreasGrid;